<template>
  <label :for="resolvedId" :class="labelClasses">
    <span class="relative inline-flex flex-shrink-0 items-center justify-center">
      <input
        :id="resolvedId"
        ref="input"
        type="checkbox"
        :name="name"
        :value="value"
        :checked="checked"
        :disabled="disabled"
        :required="required"
        :aria-invalid="invalid ? 'true' : undefined"
        :aria-describedby="describedBy"
        class="peer sr-only"
        @change="onChange"
      />
      <span aria-hidden="true" :class="boxClasses">
        <BaseIcon
          v-if="checked && !indeterminate"
          name="check"
          :size="iconSize"
          :stroke-width="3"
          class="text-ink-inverse"
        />
        <BaseIcon
          v-else-if="indeterminate"
          name="minus"
          :size="iconSize"
          :stroke-width="3"
          class="text-ink-inverse"
        />
      </span>
    </span>
    <span v-if="$slots.default || label" class="text-sm leading-5 text-ink">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { useId } from '@/composables/useId';

const labelVariants = cva('inline-flex items-start gap-3', {
  variants: {
    disabled: {
      true: 'cursor-not-allowed opacity-60',
      false: 'cursor-pointer',
    },
  },
  defaultVariants: { disabled: false },
});

const boxVariants = cva(
  [
    'inline-flex items-center justify-center rounded-xs border transition-colors duration-150',
    'peer-checked:bg-accent peer-checked:border-accent peer-indeterminate:bg-accent peer-indeterminate:border-accent',
    'peer-focus-visible:shadow-[var(--shadow-ring-focus)]',
  ],
  {
    variants: {
      size: {
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
      },
      invalid: {
        true: 'border-stroke-danger',
        false: 'border-stroke',
      },
    },
    defaultVariants: {
      size: 'md',
      invalid: false,
    },
  },
);

export type CheckboxSize = NonNullable<VariantProps<typeof boxVariants>['size']>;

interface Props {
  checked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  id?: string | null;
  name?: string;
  value?: string;
  size?: CheckboxSize;
  label?: string;
  describedBy?: string;
}

const props = withDefaults(defineProps<Props>(), {
  checked: false,
  indeterminate: false,
  disabled: false,
  invalid: false,
  required: false,
  id: null,
  size: 'md',
  label: '',
});

const emit = defineEmits<{
  (e: 'update:checked', value: boolean): void;
  (e: 'change', value: boolean): void;
}>();

const fallbackId = useId('checkbox');
const input = ref<HTMLInputElement | null>(null);

const resolvedId = computed(() => props.id ?? fallbackId);
const labelClasses = computed(() => labelVariants({ disabled: props.disabled }));
const boxClasses = computed(() => boxVariants({ size: props.size, invalid: props.invalid }));
const iconSize = computed<12 | 14>(() => (props.size === 'sm' ? 12 : 14));

function syncIndeterminate(): void {
  if (input.value) {
    input.value.indeterminate = props.indeterminate;
  }
}

function onChange(event: Event): void {
  const target = event.target as HTMLInputElement;
  emit('update:checked', target.checked);
  emit('change', target.checked);
}

watch(() => props.indeterminate, syncIndeterminate);

onMounted(syncIndeterminate);
</script>
